import React from "react";
import { PlusMinus } from "@/components/PlusMinus";
import { PriceDisplay } from "@/features/product/PriceDisplay";
import { useCartUiActions } from "./cartStore";
import {
  cartItemSelect,
  shopifyCartApi,
  ShopifyCartItem,
} from "@/services/shopifyCart";
import { ProductCard } from "../product/ProductCard";
import { SizedImage } from "@/components/SizedImage";
import { giftWithPurchase } from "@/helpers/data/cartGiftProductsData";
import { Money, $ } from "@/helpers/Money";
import { DroppsProduct, productSelect } from "@/store";
import { Page } from "./CartDrawer";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

interface CartProductProps {
  item: ShopifyCartItem & { shopifyProduct?: DroppsProduct };
  setOpenDrawer: (page: Page) => void;
}

const subscriptionText = (item: ShopifyCartItem) => {
  const subscription = cartItemSelect.subscription(item);
  return subscription.kind === "selling-plan" ? subscription.name : "One-Time Purchase";
};

export const CartProduct = ({ item, setOpenDrawer }: CartProductProps) => {
  const { closeDrawer } = useCartUiActions();
  const cartUpdater = shopifyCartApi.useChange();
  const shopifyCart = shopifyCartApi.useFetch();
  const cart = shopifyCart.data;

  const getStandardPrice = () => {
    const defaultVariant = item?.shopifyProduct?.variants.find(
      (variant) => variant.default_variant_multiplier === 1
    );

    const selectedVariant =
      item.shopifyProduct &&
      productSelect.selectedVariant(item.shopifyProduct, item.variant_id);
    if (selectedVariant) {
      return selectedVariant.price.mul(item.quantity);
    }
// fallback if no variant data
    return item.price.mul(item.quantity);
  };

  const productPageUrl = cartItemSelect.productUrl(item);
  const { active: gwpActive, products: gwpProducts } = giftWithPurchase;
  const promoIds = gwpProducts.map((product) => product.variantId);
  const isPromoGift = promoIds.includes(item.variant_id);
  const limitPerOrder = gwpActive && isPromoGift;
  const activePromo = limitPerOrder && item.quantity <= 1;
  const cartValue = cart?.items_subtotal_price ?? Money.zero;

  const baseStandardPrice = getStandardPrice();

  const selectedVariant =
    item.shopifyProduct &&
    productSelect.selectedVariant(item.shopifyProduct, item.variant_id);

  const sellingPlanAllocation = item?.selling_plan_allocation;
  const compareAtPriceCents = sellingPlanAllocation?.compare_at_price;
  const compareAtLinePrice = compareAtPriceCents != null
    ? Money.from(compareAtPriceCents, "¢").mul(item.quantity)
    : null;

  const hasDiscount =
    compareAtLinePrice != null &&
    item.final_line_price.asCents() < compareAtLinePrice.asCents();
  const displayStandardPrice = hasDiscount ? compareAtLinePrice : baseStandardPrice;

  const cartItem = {
    title: item.product_title,
    featuredImage: {
      src: item.image,
      alt: item.title,
      width: 80,
      height: 80,
    },
    size: cartItemSelect.sizing(item),
    subscription:
      activePromo && cartValue && cartValue >= $`30`
        ? "Free with $30+ order"
        : subscriptionText(item),
  };

  const sellingPlan = sellingPlanAllocation?.selling_plan;
  const discountValue =
    sellingPlan?.price_adjustments && sellingPlan?.price_adjustments.length > 0
      ? sellingPlan.price_adjustments[0].value
      : null;

  return (
    <div className="flex flex-col">
      <div className="flex items-start justify-between">
        <ProductCard
          compact
          hoverable={false}
          rounded={false}
          fullwidth
          productCardValue={cartItem}
          cover={
            cartItem.featuredImage?.src ? (
              <a href={productPageUrl} onClick={closeDrawer}>
                <div className="w-20 shrink-0 [&>img]:max-w-full">
                  <SizedImage
                    src={cartItem.featuredImage.src}
                    alt={cartItem.title}
                    height={80}
                    width={80}
                  />
                </div>
              </a>
            ) : undefined
          }
          productTitle={
            <a href={productPageUrl} onClick={closeDrawer}>
              <h3
                data-id="product-card-title"
                className="title text-sm transition hover:underline"
              >
                {productSelect.name(cartItem)}
              </h3>
            </a>
          }
        />
      </div>
      <div className="flex flex-col px-4 pb-4">
        <div className="flex items-center justify-between">
          <PlusMinus
            value={item.quantity}
            decrement={() => {
              const newQuantity = Math.max(0, item.quantity - 1);
              cartUpdater.mutate({ id: item.key, quantity: newQuantity });
            }}
            increment={() => {
              const newQuantity = item.quantity + 1;
              cartUpdater.mutate({ id: item.key, quantity: newQuantity });
            }}
            enableTrash
            min={1}
          />
          <PriceDisplay
            standardPrice={displayStandardPrice}
            discountPrice={hasDiscount ? item.final_line_price : undefined}
            showDiscountPrice={hasDiscount}
          />
        </div>
        {discountValue != null && discountValue > 0 && discountValue !== 100 && (
          <div className="self-end text-xs font-semibold text-secondary-700">
            {`${discountValue}% OFF`}
          </div>
        )}
        {item.shopifyProduct?.bundledItems?.length && (
          <div
            role="button"
            tabIndex={0}
            className="flex cursor-pointer items-center pt-1 underline"
            onKeyDown={() =>
              setOpenDrawer({
                kind: "bundle-drawer",
                bundledProducts: item.shopifyProduct?.bundledItems ?? [],
                item,
              })
            }
            onClick={() => {
              setOpenDrawer({
                kind: "bundle-drawer",
                bundledProducts: item.shopifyProduct?.bundledItems ?? [],
                item,
              });
            }}
          >
            <p className="mb-0 text-sm">What&apos;s included</p>
            <KeyboardArrowDownIcon />
          </div>
        )}
      </div>
    </div>
  );
};
